import { useEffect, useState } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import Grid from '@mui/material/Grid2';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import styled from '@mui/material/styles/styled';
import { find } from 'lodash';
import PropTypes from 'prop-types';
import { useForm, Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { object, string, mixed } from 'yup';

import { BACKUP_LABELS, BACKUP_TYPES } from '../../../../constants';
import { useUser } from '../../../../contexts/UserContext';
import { formatDateTimeUTC } from '../../../../utils/timeUtils';
import {
  match,
  sentenceCase,
  isSolrCloud,
  isVersionCompatible,
} from '../../../../utils/utils';
import Text from '../../../elements/text/Text';
import Modal from '../../../sections/modal/Modal';

const StyledCard = styled(Card)(({ theme }) => ({
  overflow: 'auto',
  marginBottom: theme.spacing(1),
  marginRight: theme.spacing(1),
}));

const StyledTitleBox = styled('div')({
  display: 'flex',
  alignItems: 'center',
  width: '49%',
});

const RestoreModal = ({
  open,
  backup,
  onRestore,
  getInstances,
  getCollections,
  onClose,
  isFile,
}) => {
  const { t } = useTranslation();
  const { user } = useUser();

  const titles = [
    t('backupSubject', { ns: 'fields' }),
    t('instance', { ns: 'instance' }),
    t('backup.executeDate', { ns: 'project' }),
    t('backupType', { ns: 'fields' }),
  ];

  const indexTitles = [t('collection', { ns: 'instance' })];
  const fileTitles = [
    t('nodeNumber', { ns: 'instance' }),
    [
      t('includedFiles', { ns: 'fields' }),
      t('excludedFiles', { ns: 'fields' }),
    ],
  ];

  const data = [
    backup?.subject,
    backup?.instanceSetName,
    backup?.createDate,
    backup?.category,
  ];

  const indexData = [backup?.collection];
  const fileData = [
    backup?.instanceSubscript,
    [backup?.includeFiles, backup?.excludeFiles],
  ];

  const [instanceList, setInstanceList] = useState([]);
  const [collectionList, setCollectionList] = useState([]);
  const [versionWarning, setVersionWarning] = useState('');

  const schema = object({
    instance: string().required(
      sentenceCase(
        t('selectRequired', {
          ns: 'validations',
          field: t('instance', { ns: 'instance' }),
        })
      )
    ),
    collection: mixed().test(
      'collectionRequired',
      sentenceCase(
        t('selectRequired', {
          ns: 'validations',
          field: t('collection', { ns: 'instance' }),
        })
      ),
      (val) => (!isFile ? !!val : true)
    ),
  });

  const {
    handleSubmit,
    control,
    watch,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
  });

  const watchInstance = watch('instance');

  const getInstanceData = async () => {
    try {
      const instanceRes = await getInstances();
      setInstanceList(instanceRes);
    } catch {
      setInstanceList([]);
    }
  };

  const getCollectionData = async () => {
    try {
      const chosenInstance = match(instanceList, watchInstance, 'id');
      const collectionsRes = await getCollections(chosenInstance);
      setCollectionList(collectionsRes);
    } catch {
      setCollectionList([]);
    }
  };

  useEffect(() => {
    if (!backup) return;
    getInstanceData();
  }, [backup]);

  useEffect(() => {
    if (isFile || !watchInstance) return;

    const chosenInstance = match(instanceList, watchInstance, 'id');

    const isCompatible = isVersionCompatible(
      backup.solrVersion,
      chosenInstance.solrVersion
    );
    setVersionWarning(
      isCompatible
        ? ''
        : t('restoreVersionDiffWarning', {
            ns: 'descriptions',
            origin: backup.solrVersion,
            target: chosenInstance.solrVersion,
          })
    );

    setValue('collection', '');
    getCollectionData();
  }, [watchInstance]);

  const handleRestore = (data) => {
    const col = data.collection || backup.collection;
    onRestore(data.instance, col, isFile);
  };

  const handleClose = () => onClose();

  return (
    <Modal
      title={
        isFile
          ? t('backup.fileRestore', { ns: 'project' })
          : t('backup.indexRestore', { ns: 'project' })
      }
      openModal={open}
      handleClose={handleClose}
    >
      {titles.map((section, index) => (
        <Box sx={{ display: 'flex', mb: 1 }} key={index}>
          <StyledTitleBox>
            <Text bodyBold>{section}</Text>
          </StyledTitleBox>
          <Box>
            {section === t('backup.executeDate', { ns: 'project' }) ? (
              <Text data-testid={data[index]}>
                {data[index]
                  ? formatDateTimeUTC(data[index], user?.timeZone)
                  : '-'}
              </Text>
            ) : section === t('backupType', { ns: 'fields' }) ? (
              <Text data-testid={data[index]}>
                {t(find(BACKUP_LABELS, { value: data[index] })?.label, {
                  ns: 'menus',
                })}
              </Text>
            ) : (
              <Text data-testid={data[index]}>{data[index]}</Text>
            )}
          </Box>
        </Box>
      ))}
      {!isFile &&
        indexTitles.map((section, index) => (
          <Box sx={{ display: 'flex', mb: 2 }} key={index}>
            <StyledTitleBox>
              <Text bodyBold>{section}</Text>
            </StyledTitleBox>
            <Box sx={{ mt: 0 }}>
              <Text data-testid={indexData[index]}>{indexData[index]}</Text>
            </Box>
          </Box>
        ))}
      {isFile &&
        fileTitles.map((section, index) =>
          index === 0 ? (
            isSolrCloud(backup.solrType) &&
            backup.category === BACKUP_TYPES.FILE && (
              <Box sx={{ display: 'flex', mb: 1 }} key={index}>
                <StyledTitleBox>
                  <Text bodyBold>{section}</Text>
                </StyledTitleBox>
                <Box sx={{ mt: 0 }}>
                  <Text data-testid={fileData[index]}>{fileData[index]}</Text>
                </Box>
              </Box>
            )
          ) : (
            <Box key={index}>
              {/* Included && Excluded Files */}
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'space-between',
                }}
              >
                {fileTitles[index].map((section, fileIndex) => (
                  <Box
                    sx={{ flexDirection: 'column', width: '50%' }}
                    key={fileIndex}
                  >
                    <Text bodyBold style={{ fontSize: '0.75em' }}>
                      {section}
                    </Text>
                    <StyledCard variant="outlined" aria-label="preview card">
                      <Box sx={{ p: 1, height: '5em', overflow: 'auto' }}>
                        <Text style={{ whiteSpace: 'pre-wrap' }}>
                          {fileData[index][fileIndex]}
                        </Text>
                      </Box>
                    </StyledCard>
                  </Box>
                ))}
              </Box>

              {/* Backed Up Files */}
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <Text bodyBold style={{ fontSize: '0.75em' }}>
                  {t('backup.backedUpFiles', { ns: 'project' })}
                </Text>
                <StyledCard variant="outlined" aria-label="preview card">
                  <Box sx={{ height: '5em', overflow: 'auto', p: 1 }}>
                    <Text sx={{ whiteSpace: 'pre-wrap' }}>{backup?.files}</Text>
                  </Box>
                </StyledCard>
              </Box>
            </Box>
          )
        )}
      <form onSubmit={handleSubmit(handleRestore)}>
        <Grid container spacing={2}>
          {versionWarning && <Alert severity="warning">{versionWarning}</Alert>}
          {/* Instance Select */}
          <Grid size={12}>
            <FormControl fullWidth error={!!errors.instance}>
              <InputLabel>
                {t('backup.restoreTo', {
                  ns: 'project',
                  text: t('instance', { ns: 'instance' }),
                })}
              </InputLabel>
              <Controller
                control={control}
                name="instance"
                shouldUnregister
                defaultValue=""
                render={({ field }) => (
                  <Select
                    {...field}
                    label={t('backup.restoreTo', {
                      ns: 'project',
                      text: t('instance', { ns: 'instance' }),
                    })}
                    inputProps={{ 'aria-label': 'instance-dropdown' }}
                  >
                    {instanceList?.map((i) => (
                      <MenuItem value={i.id} key={i.id}>
                        {i.name}
                      </MenuItem>
                    ))}
                  </Select>
                )}
              />
              <FormHelperText>{errors.instance?.message}</FormHelperText>
            </FormControl>
          </Grid>

          {/* Collection Select */}
          {!isFile && watchInstance && (
            <Grid mt={2} size={12}>
              <FormControl fullWidth error={!!errors.collection}>
                <InputLabel>
                  {t('backup.restoreTo', {
                    ns: 'project',
                    text: t('collection', { ns: 'instance' }),
                  })}
                </InputLabel>
                <Controller
                  control={control}
                  name="collection"
                  shouldUnregister
                  defaultValue=""
                  render={({ field }) => (
                    <Select
                      {...field}
                      label={t('backup.restoreTo', {
                        ns: 'project',
                        text: t('collection', { ns: 'instance' }),
                      })}
                      inputProps={{ 'aria-label': 'collection-dropdown' }}
                    >
                      {collectionList?.map((c) => (
                        <MenuItem value={c} key={c}>
                          {c}
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                />
                <FormHelperText>{errors.collection?.message}</FormHelperText>
              </FormControl>
            </Grid>
          )}
        </Grid>
        <Box sx={{ display: 'flex', mb: 1, mt: 3, justifyContent: 'center' }}>
          <Button type="submit">{t('restore', { ns: 'titles' })}</Button>
        </Box>
      </form>
    </Modal>
  );
};

RestoreModal.propTypes = {
  backup: PropTypes.object,
  getInstances: PropTypes.func,
  getCollections: PropTypes.func,
  onRestore: PropTypes.func,
  onClose: PropTypes.func,
  open: PropTypes.bool,
  isFile: PropTypes.bool,
};

export default RestoreModal;
